/**
 * Signed-in users who haven't agreed to the current Terms version (Google
 * sign-ups, accounts from before Oct 27 2026, or after TERMS_VERSION is bumped)
 * see this before using the app: an unchecked "I am 21+ and agree" checkbox,
 * then "Agree and continue" (writes users/{uid}.termsAcceptance) or "Sign out".
 * Legal and auth screens stay reachable so the terms can be read first.
 */
import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import ConsentCheckbox, { hasCurrentConsent, termsAcceptanceRecord, CONSENT_REQUIRED_ERROR } from './ConsentCheckbox';

export const CONSENT_EXEMPT_SCREENS = new Set(['Terms', 'Privacy', 'Login', 'Register', 'ForgotPassword', 'ResetPassword', 'AdminLogin', 'Suspended', 'MainPage']);

export function needsConsent(user, screen) {
  if (!user?.uid || CONSENT_EXEMPT_SCREENS.has(screen)) return false;
  return !hasCurrentConsent(user);
}

export default function TermsConsentGate() {
  const { user, setUser, loading, logout } = useAuth();
  const { currentScreen, navigateTo } = useNavigation();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const cardRef = useRef(null);
  const open = !loading && needsConsent(user, currentScreen);

  // Move keyboard focus into the dialog when it opens (web).
  useEffect(() => {
    if (open && Platform.OS === 'web') cardRef.current?.focus?.();
  }, [open]);

  if (!open) return null;

  const agree = async () => {
    if (!checked) { setError(CONSENT_REQUIRED_ERROR); return; }
    setSaving(true);
    setError('');
    try {
      const method = user.provider === 'google' ? 'google_signin' : 'existing_account';
      await setDoc(doc(db, 'users', user.uid), { termsAcceptance: termsAcceptanceRecord(method, serverTimestamp()) }, { merge: true });
      setUser((prev) => (prev ? { ...prev, termsAcceptance: termsAcceptanceRecord(method, new Date()) } : prev));
    } catch (e) {
      console.warn('[TermsConsentGate] save failed:', e?.message || e);
      setError("Couldn't save that. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.overlay} accessibilityViewIsModal {...(Platform.OS === 'web' ? { role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'consent-title' } : {})}>
      <View style={styles.card} ref={cardRef} {...(Platform.OS === 'web' ? { tabIndex: -1 } : {})}>
        <Text style={styles.title} nativeID="consent-title" accessibilityRole="header">Before you continue</Text>
        <Text style={styles.body}>
          BetOptimized is for adults 21 and older. We updated our Terms of Service and Privacy Policy on October 27, 2026 (arbitration in California, subscriptions and cancellation, AI-assisted search, and how we handle your data).
        </Text>
        <ConsentCheckbox checked={checked} onChange={(v) => { setChecked(v); if (v) setError(''); }} disabled={saving} navigateTo={navigateTo} invalid={!!error && !checked} nativeID="consent-gate-checkbox" />
        {error ? <Text style={styles.error} accessibilityRole="alert">{error}</Text> : null}
        <View style={styles.actions}>
          <Pressable onPress={() => logout()} disabled={saving} accessibilityRole="button" style={({ hovered }) => [styles.secondary, hovered && styles.secondaryHover]}>
            <Text style={styles.secondaryText}>Sign out</Text>
          </Pressable>
          <Pressable onPress={agree} disabled={saving} accessibilityRole="button" accessibilityState={{ disabled: saving }} style={({ hovered }) => [styles.primary, !checked && styles.primaryMuted, hovered && checked && styles.primaryHover]}>
            <Text style={styles.primaryText}>{saving ? 'Saving…' : 'Agree and continue'}</Text>
          </Pressable>
        </View>
        <Text style={styles.help}>Gambling problem? Call 1-800-GAMBLER (1-800-426-2537).</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9000,
    backgroundColor: 'rgba(0,0,0,0.72)', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 16,
  },
  card: {
    width: '100%', maxWidth: 460, borderRadius: 16, padding: 24, gap: 14,
    backgroundColor: '#0E1117', borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)',
  },
  title: { color: '#fff', fontSize: 20, fontWeight: '700' },
  body: { color: 'rgba(255,255,255,0.72)', fontSize: 14, lineHeight: 21 },
  error: { color: '#F87171', fontSize: 13 },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 4, flexWrap: 'wrap' },
  primary: { backgroundColor: '#3B82F6', borderRadius: 10, paddingVertical: 11, paddingHorizontal: 18, ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}) },
  primaryMuted: { opacity: 0.55 },
  primaryHover: { backgroundColor: '#2563EB' },
  primaryText: { color: '#fff', fontSize: 14, fontWeight: '600' },
  secondary: { borderRadius: 10, paddingVertical: 11, paddingHorizontal: 16, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)', ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}) },
  secondaryHover: { backgroundColor: 'rgba(255,255,255,0.06)' },
  secondaryText: { color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: '500' },
  help: { color: 'rgba(255,255,255,0.45)', fontSize: 12 },
});
